import { useEffect, useMemo, useState } from 'react';
import { BlockStack, Card, DataTable, SkeletonBodyText, Text } from '@shopify/polaris';
import { formatToCurrency, getRequest } from '../../../../shared/components/functions';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { Delta } from './Delta';
import BusinessUnitFilter, { businessUnitParam, useBusinessUnitFilter, useBusinessUnitName } from './BusinessUnitFilter';
import { formatInteger, formatPercent, toNumber } from './analyticsHelpers';
import RetentionCard from './RetentionCard';
import MarginHeatmapCard from './MarginHeatmapCard';

export default function KpiTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessUnitId, setBusinessUnitId] = useBusinessUnitFilter();
  const unitName = useBusinessUnitName(businessUnitId);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      const result = await getRequest(`/api/metrics/yearly-kpis${businessUnitParam(businessUnitId, true)}`);
      if (cancelled) return;
      setData(Array.isArray(result) ? result : []);
      setLoading(false);
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [businessUnitId]);

  const rows = useMemo(() => {
    const enriched = data.map((row) => {
      const netRevenueExcl = toNumber(row.revenue_excl) - toNumber(row.credited_out_excl);
      const netCostsExcl = toNumber(row.costs_excl) - toNumber(row.credited_in_excl);
      return {
        ...row,
        netRevenueExcl,
        netCostsExcl,
        grossMargin: netRevenueExcl - netCostsExcl
      };
    });

    const byYear = {};
    enriched.forEach((row) => {
      byYear[row.year] = row;
    });

    return enriched
      .sort((a, b) => b.year - a.year)
      .map((row) => {
        const previous = byYear[row.year - 1];
        const invoiceCount = toNumber(row.invoices_out_count);
        const customers = toNumber(row.active_customers);
        const isCurrentYear = Number(row.year) === new Date().getFullYear();
        return [
          <b>{isCurrentYear ? `${row.year} (YTD)` : row.year}</b>,
          formatToCurrency(row.netRevenueExcl),
          <Delta current={row.netRevenueExcl} previous={previous?.netRevenueExcl} />,
          formatToCurrency(row.netCostsExcl),
          <Delta current={row.netCostsExcl} previous={previous?.netCostsExcl} invert />,
          <Text tone={row.grossMargin >= 0 ? 'success' : 'critical'} as="span">
            {formatToCurrency(row.grossMargin)}
          </Text>,
          row.netRevenueExcl ? formatPercent(row.grossMargin / row.netRevenueExcl) : '—',
          formatInteger(invoiceCount),
          invoiceCount ? formatToCurrency(row.netRevenueExcl / invoiceCount) : '—',
          formatInteger(customers),
          formatInteger(row.new_customers),
          customers ? formatToCurrency(row.netRevenueExcl / customers) : '—',
          formatInteger(row.invoices_in_count),
          formatInteger(row.active_suppliers)
        ];
      });
  }, [data]);

  return (
    <BlockStack gap="400">
      <BusinessUnitFilter value={businessUnitId} onChange={setBusinessUnitId} />
      <Card>
        <CardHeading title={unitName ? `Yearly report — ${unitName}` : 'Yearly report — the state of the business'} />
        <Text tone="subdued" as="p">
          One row per year with the numbers that matter: what you sold, what it cost, what stuck to the ribs, and how broad your customer base was.
        </Text>
        <div style={{ marginTop: '12px' }}>
          {loading ? (
            <SkeletonBodyText lines={12} />
          ) : (
            <DataTable
              verticalAlign="middle"
              increasedTableDensity
              stickyHeader
              columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
              headings={[
                <b>Year</b>,
                <b>Net revenue (excl.)</b>,
                <b>Growth</b>,
                <b>Net costs (excl. VAT)</b>,
                <b>Δ costs</b>,
                <b>Gross margin</b>,
                <b>Margin %</b>,
                <b>Invoices out</b>,
                <b>Avg invoice</b>,
                <b>Customers</b>,
                <b>New</b>,
                <b>Revenue / customer</b>,
                <b>Invoices in</b>,
                <b>Suppliers</b>
              ]}
              rows={rows}
            />
          )}
        </div>
      </Card>
      <MarginHeatmapCard businessUnitId={businessUnitId} titleSuffix={unitName ? ` — ${unitName}` : ''} />
      <RetentionCard businessUnitId={businessUnitId} titleSuffix={unitName ? ` — ${unitName}` : ''} />
      <Card>
        <CardHeading title="How these numbers are calculated" />
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14 }}>
          <li>
            <b>Net revenue (excl.)</b>: outgoing invoice lines excl. VAT (same status filter as the gross margin graph), minus outgoing credit notes excl. VAT.
          </li>
          <li>
            <b>Net costs (excl. VAT)</b>: incoming invoices by booked date, using the excl. VAT amount stored on each invoice and credit note (exact for Peppol documents, equal to the total for
            VAT-free ones), minus incoming credit notes.
          </li>
          <li>
            <b>Gross margin</b>: net revenue minus net costs. Matches the bruto_marge curve on the Overview tab.
          </li>
          <li>
            <b>Customers / Suppliers</b>: distinct companies invoiced (or invoicing you) that year. <b>New</b> = customers whose very first invoice was in that year.
          </li>
          <li>
            <b>Avg invoice</b>: net revenue divided by the number of outgoing invoices.
          </li>
        </ul>
      </Card>
    </BlockStack>
  );
}
